import React from "react";

import {
  LineChart,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  Bar
} from "recharts";

import { AnalyticsCard } from "../AnalyticsCard";
import { DataPointCard } from "../DataPointCard";
import { HollowTable, HollowTableRow } from "../HollowTable";
import { Icon } from "../../atoms/Icon";
import currency from "currency-formatter";

import useReportingProduct from "./ReportingProductHooks";
import styles from "./ReportingOverview.module.scss";
import FormatHelpers from "./FormatHelpers";

const ReportingProduct = ({ productReport, dateRange }) => {
  const {
    totalNumberOfVideos,
    totalNumberOfViews,
    totalNumberOfSales,
    totalSalesInDollars,
    totalVideosViewsGraphData,
    totalVideosViewsColor,
    pinkButtonClicksGraphData,
    pinkButtonColor,
    totalSalesCountGraphData,
    totalDollarValueOfSalesGraphData,
    videoOriginListTableData
  } = useReportingProduct(productReport, dateRange);

  return (
    <div className={styles.reportingOverviewContainer}>
      <div className={styles.dataPointCardsContainer}>
        <DataPointCard
          color="#09A496"
          iconName="grouped-bar-chart"
          title="TOTAL # OF VIDEOS"
          value={totalNumberOfVideos}
        />
        <DataPointCard
          color="#FF5D78"
          iconName="eye-open"
          title="TOTAL # OF VIEWS"
          value={totalNumberOfViews}
        />
        <DataPointCard
          color="#09A496"
          iconName="timeline-line-chart"
          title="TOTAL # OF SALES"
          value={totalNumberOfSales}
        />
        <DataPointCard
          color="#FF5D78"
          iconName="Cash"
          title="TOTAL SALES IN $"
          value={`$${currency.format(totalSalesInDollars, { format: "USD" })}`}
        />
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Total Views for All Videos">
          <LineChart width={552} height={285} data={totalVideosViewsGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="category"
              tickFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange,
                  format: "MMM YY"
                })
              }
            />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip
              labelFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange
                })
              }
            />
            <Line
              type="monotone"
              dataKey="viewCount"
              stroke={totalVideosViewsColor}
              name="# of views"
            />
          </LineChart>
        </AnalyticsCard>
        <AnalyticsCard title="Purchase Intent Clicks">
          <BarChart width={552} height={285} data={pinkButtonClicksGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="duration"
              type="number"
              allowDecimals={false}
              domain={[0, "auto"]}
              tickFormatter={FormatHelpers.durationFormatter}
            />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip labelFormatter={FormatHelpers.durationFormatter} />
            <Bar
              dataKey="count"
              fill={pinkButtonColor}
              name="# of clicks"
              barSize={4}
            />
          </BarChart>
        </AnalyticsCard>
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Total # of Sales">
          <LineChart width={552} height={285} data={totalSalesCountGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={["auto", "auto"]}
              interval="preserveStartEnd"
              scale="time"
              tickFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange,
                  format: "MMM YY"
                })
              }
            />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip
              labelFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange
                })
              }
            />
            <Line
              type="monotone"
              dataKey="salesCount"
              stroke={totalVideosViewsColor}
              name="# of sales"
            />
            )}
          </LineChart>
        </AnalyticsCard>
        <AnalyticsCard title="Total Value of Sales ($)">
          <LineChart
            width={552}
            height={285}
            data={totalDollarValueOfSalesGraphData}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={["auto", "auto"]}
              interval="preserveStartEnd"
              scale="time"
              tickFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange,
                  format: "MMM YY"
                })
              }
            />
            <YAxis
              type="number"
              tickFormatter={(amount) => `$${amount.toFixed(2)}`}
            />
            <Tooltip
              labelFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange
                })
              }
            />
            <Line
              type="monotone"
              dataKey="salesAmount"
              stroke={totalVideosViewsColor}
              name="$ in sales"
            />
          </LineChart>
        </AnalyticsCard>
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Video Origins List">
          <div className={styles.tableCardContainer}>
            <HollowTable titles={["Views", "Video Origin"]}>
              {videoOriginListTableData.map(({ id, views, origin }) => (
                <HollowTableRow key={id}>
                  <span className={styles.darkTableText}>
                    <Icon name="eye-open" color="#506883" size={15} />
                    {views}
                  </span>
                  {origin === "Other" ? (
                    <p>Other</p>
                  ) : (
                    <a
                      href={
                        origin.startsWith("https://") ||
                        origin.startsWith("http://")
                          ? origin
                          : `https://${origin}`
                      }
                      target="_blank"
                    >
                      {origin}
                    </a>
                  )}
                </HollowTableRow>
              ))}
            </HollowTable>
          </div>
        </AnalyticsCard>
      </div>
    </div>
  );
};

export default ReportingProduct;
